import { BOSS_IDS } from "./bosses.ts";
import { CREATURE_BY_ID } from "./creatures.ts";
import { DIFFICULTIES, DIFFICULTY_NAMES, MAX_PLAYERS, MODES, PLAYER_LIMITS, type Difficulty, type Mode } from "./match.ts";
import { GUARDIAN_COLORS } from "./pois.ts";

/**
 * Resultado de partida e conquistas (doc 06, "Conquistas e estatísticas"; ADR 0005; roadmap F32).
 * O servidor de partidas monta o resultado; a API valida, grava e calcula as conquistas por este
 * catálogo (o mesmo código nos dois lados, para não divergir quando o balanceamento mudar).
 */
export const ACHIEVEMENTS_VERSION = 1;

/** Contadores de um jogador numa partida (os do schema e os que só o servidor conhece). */
export interface MatchPlayerStats {
  kills: number;
  deaths: number;
  revives: number;
  dealt: number;
  taken: number;
  simpatias: number;
  chests: number;
  coins: number;
  /** Itens depositados na Jangada. */
  raft: number;
  whirlwinds: number;
  pranks: number;
  /** Chefes que este jogador derrubou (tipo) e Muiraquitãs que conquistou (cor). */
  bosses: string[];
  guardians: string[];
  /** Achou os destroços da Jangada. */
  raftFound: boolean;
}

export type MatchOutcome = "vitoria" | "derrota" | "treta";

export interface MatchResult {
  /** Id da partida (UUID): a gravação é idempotente por ele. */
  id: string;
  mode: Mode;
  difficulty: Difficulty;
  seed: string;
  /** Versão do gerador (A13): rankings por seed só se comparam dentro dela. */
  genVersion: number;
  seconds: number;
  /** Dia em que a partida acabou. */
  day: number;
  outcome: MatchOutcome;
  players: {
    /** Conta do jogador (do JWT validado pelo servidor); convidado não tem e não pontua. */
    userId?: string;
    name: string;
    winner: boolean;
    /** Dia que este jogador alcançou (quem saiu de vez fica com o dia da saída). */
    day: number;
    stats: MatchPlayerStats;
  }[];
}

/** Agregados vitalícios de um jogador (calculados das partidas gravadas). */
export interface LifetimeStats {
  matches: number;
  wins: number;
  winsByDifficulty: Record<Difficulty, number>;
  tretaWins: number;
  tretaKills: number;
  kills: number;
  revives: number;
  whirlwinds: number;
  bestDay: number;
  bosses: Record<string, number>;
  guardians: Record<string, number>;
  raftFound: number;
}

export const emptyLifetime = (): LifetimeStats => ({
  matches: 0,
  wins: 0,
  winsByDifficulty: { tranquilo: 0, arretado: 0, sinistro: 0 },
  tretaWins: 0,
  tretaKills: 0,
  kills: 0,
  revives: 0,
  whirlwinds: 0,
  bestDay: 0,
  bosses: {},
  guardians: {},
  raftFound: 0,
});

/** Soma uma partida do jogador aos agregados (puro). */
export function accumulate(base: LifetimeStats, match: Pick<MatchResult, "mode" | "difficulty">, player: { winner: boolean; day: number; stats: MatchPlayerStats }): LifetimeStats {
  const s = player.stats;
  const out: LifetimeStats = { ...base, winsByDifficulty: { ...base.winsByDifficulty }, bosses: { ...base.bosses }, guardians: { ...base.guardians } };
  out.matches++;
  out.kills += s.kills;
  out.revives += s.revives;
  out.whirlwinds += s.whirlwinds;
  out.bestDay = Math.max(out.bestDay, player.day);
  if (s.raftFound) out.raftFound++;
  for (const b of s.bosses) out.bosses[b] = (out.bosses[b] ?? 0) + 1;
  for (const g of s.guardians) out.guardians[g] = (out.guardians[g] ?? 0) + 1;
  if (match.mode === "treta") {
    out.tretaKills += s.kills;
    if (player.winner) out.tretaWins++;
  } else if (player.winner) {
    out.wins++;
    out.winsByDifficulty[match.difficulty]++;
  }
  return out;
}

export type AchievementCategory = "progresso" | "desafio" | "caca" | "cooperacao" | "saci" | "exploracao" | "treta";

export interface AchievementContext {
  match: MatchResult;
  player: MatchResult["players"][number];
  /** Agregados já com esta partida. */
  lifetime: LifetimeStats;
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  category: AchievementCategory;
  check: (c: AchievementContext) => boolean;
}

const mutiraoWin = (c: AchievementContext) => c.match.mode === "mutirao" && c.match.outcome === "vitoria" && c.player.winner;
const NIGHT_BOSSES = BOSS_IDS;
const COLORS = GUARDIAN_COLORS.map((g) => g.color);

/**
 * Catálogo (doc 06, categorias). Hipóteses de balanceamento, centralizadas aqui. Mandinga e
 * travessuras por tipo ficam para quando o servidor contar esses detalhes.
 */
export const ACHIEVEMENTS: readonly Achievement[] = [
  ...DIFFICULTIES.map(
    (d): Achievement => ({ id: `vitoria-${d}`, name: `Vitória no ${DIFFICULTY_NAMES[d]}`, description: `Fugir da ilha no ${DIFFICULTY_NAMES[d]}.`, category: "progresso", check: (c) => mutiraoWin(c) && c.match.difficulty === d }),
  ),
  { id: "fuga-relampago", name: "Fuga Relâmpago", description: "Fugir da ilha até o dia 5.", category: "desafio", check: (c) => mutiraoWin(c) && c.match.day <= 5 },
  { id: "sem-simpatias", name: "Sem Bênção", description: "Fugir sem que ninguém do grupo tenha Bênçãos.", category: "desafio", check: (c) => mutiraoWin(c) && c.match.players.every((p) => p.stats.simpatias === 0) },
  { id: "sem-arranhao", name: "Sem Arranhão", description: "Fugir sem o grupo levar dano (de 1 a 4 jogadores).", category: "desafio", check: (c) => mutiraoWin(c) && c.match.players.every((p) => p.stats.taken === 0) },
  { id: "abates-100", name: "Caçador", description: "Derrubar 100 criaturas.", category: "caca", check: (c) => c.lifetime.kills >= 100 },
  { id: "abates-1000", name: "Terror da Floresta", description: "Derrubar 1000 criaturas.", category: "caca", check: (c) => c.lifetime.kills >= 1000 },
  ...NIGHT_BOSSES.map((b): Achievement => {
    const boss = CREATURE_BY_ID.get(b);
    const name = boss ? [boss.article, boss.name].filter(Boolean).join(" ") : b;
    return { id: `chefe-${b}`, name: `Contra ${name}`, description: `Derrubar ${name} (o golpe final).`, category: "caca", check: (c) => c.player.stats.bosses.includes(b) };
  }),
  { id: "todos-os-chefes", name: "Noite Limpa", description: "Derrubar todos os chefes da noite.", category: "caca", check: (c) => NIGHT_BOSSES.every((b) => (c.lifetime.bosses[b] ?? 0) > 0) },
  { id: "todos-os-guardioes", name: "Guardião dos Guardiões", description: "Conquistar as cinco Gemas.", category: "caca", check: (c) => COLORS.every((g) => (c.lifetime.guardians[g] ?? 0) > 0) },
  { id: "reviver-10", name: "Mão Amiga", description: "Reviver aliados 10 vezes.", category: "cooperacao", check: (c) => c.lifetime.revives >= 10 },
  { id: "redemoinhos-5", name: "Engarrafador", description: "Prender o Diabrete na Garrafa 5 vezes.", category: "saci", check: (c) => c.lifetime.whirlwinds >= 5 },
  { id: "jangada", name: "Achado", description: "Achar o Naufrágio.", category: "exploracao", check: (c) => c.player.stats.raftFound },
  { id: "dia-10", name: "Sobrevivente", description: "Chegar ao dia 10.", category: "exploracao", check: (c) => c.lifetime.bestDay >= 10 },
  { id: "treta-vitoria", name: "Último em Pé", description: "Vencer uma Arena.", category: "treta", check: (c) => c.match.mode === "treta" && c.player.winner },
  { id: "treta-vitorias-10", name: "Campeão da Arena", description: "Vencer 10 Arenas.", category: "treta", check: (c) => c.lifetime.tretaWins >= 10 },
  { id: "treta-abates-50", name: "Ninguém Segura", description: "Derrubar 50 jogadores e criaturas na Arena.", category: "treta", check: (c) => c.lifetime.tretaKills >= 50 },
];

export const ACHIEVEMENT_BY_ID = new Map(ACHIEVEMENTS.map((a) => [a.id, a]));

/** Conquistas novas desta partida (as que passam agora e o jogador ainda não tinha). */
export function newAchievements(c: AchievementContext, already: ReadonlySet<string>): string[] {
  return ACHIEVEMENTS.filter((a) => !already.has(a.id) && a.check(c)).map((a) => a.id);
}

/** Validação do resultado que chega à API (sem dependências: o Worker usa direto). */
export function validateMatchResult(raw: unknown): { ok: true; value: MatchResult } | { ok: false; error: string } {
  const fail = (error: string) => ({ ok: false as const, error });
  if (!raw || typeof raw !== "object") return fail("corpo");
  const r = raw as Record<string, unknown>;
  const str = (v: unknown, max: number) => typeof v === "string" && v.length > 0 && v.length <= max;
  const int = (v: unknown, min: number, max: number) => typeof v === "number" && Number.isInteger(v) && v >= min && v <= max;
  if (!str(r["id"], 64) || !/^[0-9a-f-]{8,64}$/i.test(r["id"] as string)) return fail("id");
  if (!MODES.includes(r["mode"] as Mode)) return fail("mode");
  if (!DIFFICULTIES.includes(r["difficulty"] as Difficulty)) return fail("difficulty");
  if (!str(r["seed"], 16)) return fail("seed");
  if (!int(r["genVersion"], 0, 1_000_000)) return fail("genVersion");
  if (!int(r["seconds"], 0, 7 * 86_400)) return fail("seconds");
  if (!int(r["day"], 0, 1000)) return fail("day");
  if (!["vitoria", "derrota", "treta"].includes(r["outcome"] as string)) return fail("outcome");
  // Até 4 (F54); a Arena exige um rival (com 1 só, ele "venceria" morrendo).
  const minPlayers = PLAYER_LIMITS[r["mode"] as Mode].min;
  if (!Array.isArray(r["players"]) || r["players"].length < minPlayers || r["players"].length > MAX_PLAYERS) return fail("players");
  const players: MatchResult["players"] = [];
  const counters = ["kills", "deaths", "revives", "dealt", "taken", "simpatias", "chests", "coins", "raft", "whirlwinds", "pranks"] as const;
  for (const p of r["players"] as unknown[]) {
    if (!p || typeof p !== "object") return fail("player");
    const q = p as Record<string, unknown>;
    if (q["userId"] !== undefined && !str(q["userId"], 64)) return fail("userId");
    if (!str(q["name"], 32) || typeof q["winner"] !== "boolean" || !q["stats"] || typeof q["stats"] !== "object") return fail("player");
    if (!int(q["day"], 0, r["day"] as number)) return fail("player.day");
    const s = q["stats"] as Record<string, unknown>;
    const stats = {} as MatchPlayerStats;
    for (const k of counters) {
      if (!int(s[k], 0, 10_000_000)) return fail(`stats.${k}`);
      (stats as unknown as Record<string, number>)[k] = s[k] as number;
    }
    const list = (v: unknown, allowed: readonly string[]) => Array.isArray(v) && v.length <= 64 && v.every((x) => typeof x === "string" && allowed.includes(x));
    if (!list(s["bosses"], NIGHT_BOSSES) || !list(s["guardians"], COLORS) || typeof s["raftFound"] !== "boolean") return fail("stats.listas");
    stats.bosses = [...(s["bosses"] as string[])];
    stats.guardians = [...(s["guardians"] as string[])];
    stats.raftFound = s["raftFound"] as boolean;
    players.push({ ...(q["userId"] ? { userId: q["userId"] as string } : {}), name: q["name"] as string, winner: q["winner"] as boolean, day: q["day"] as number, stats });
  }
  if (new Set(players.filter((p) => p.userId).map((p) => p.userId)).size !== players.filter((p) => p.userId).length) return fail("userId repetido");
  return { ok: true, value: { id: r["id"] as string, mode: r["mode"] as Mode, difficulty: r["difficulty"] as Difficulty, seed: r["seed"] as string, genVersion: r["genVersion"] as number, seconds: r["seconds"] as number, day: r["day"] as number, outcome: r["outcome"] as MatchOutcome, players } };
}
